<script lang="ts">
  import {
    ArrowRight,
    CreditCard,
    Layers,
    Lock,
    Sparkles,
    Sun,
    Moon,
    Clock
  } from "lucide-svelte";
  import Button from "./ui/Button.svelte";
  import Logo from "./Logo.svelte";
  import { cn } from "./lib/utils";

  export let theme: "light" | "dark" = "dark";
  export let hasCompletedOnboarding = false;
  export let onStart: () => void;
  export let onContinue: () => void = () => {};
  export let onToggleTheme: () => void = () => {};
</script>

<div class="relative min-h-screen overflow-hidden">
  <!-- big ambient -->
  <div
    aria-hidden="true"
    class="pointer-events-none absolute inset-0 -z-10"
    style="
      background:
        radial-gradient(ellipse 50% 35% at 12% 8%, hsl(var(--primary) / 0.30), transparent 60%),
        radial-gradient(ellipse 40% 35% at 92% 22%, hsl(var(--primary) / 0.18), transparent 60%),
        radial-gradient(ellipse 60% 50% at 50% 110%, hsl(var(--primary) / 0.15), transparent 60%);
    "
  ></div>

  <div class="mx-auto flex min-h-screen max-w-3xl flex-col px-6 pt-6 pb-10">
    <!-- Top bar -->
    <header class="anim-fade-in-up flex items-center justify-between">
      <div class="flex items-center gap-2">
        <Logo size={28} />
        <div class="flex flex-col leading-tight">
          <span class="font-display text-base">魔法小卡</span>
          <span class="font-label text-muted-foreground">MAGIC CARD · BETA</span>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <button
          type="button"
          class="focus-amber inline-flex size-9 items-center justify-center rounded-full border border-input bg-background/80 text-muted-foreground transition-colors hover:text-foreground"
          aria-label="切换深色 / 浅色"
          on:click={onToggleTheme}
        >
          {#if theme === "dark"}
            <Sun class="size-4" />
          {:else}
            <Moon class="size-4" />
          {/if}
        </button>
      </div>
    </header>

    <!-- Hero -->
    <main class="flex flex-1 flex-col items-center justify-center py-12 text-center">
      <div class="anim-pop mb-6 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5">
        <Sparkles class="size-3.5 text-primary" />
        <span class="font-label text-primary">所有信用卡 · 一个默认</span>
      </div>

      <h1
        class="anim-fade-in-up font-display text-[2.2rem] leading-[1.08] tracking-tight text-foreground sm:text-[3.4rem]"
        style="animation-delay: 60ms;"
      >
        多卡用一张<br />
        <span class="whitespace-nowrap bg-gradient-to-br from-primary to-primary/60 bg-clip-text text-transparent">
          每天少做一个决策
        </span>
      </h1>

      <p
        class="anim-fade-in-up mx-auto mt-6 max-w-md text-base leading-relaxed text-muted-foreground"
        style="animation-delay: 140ms;"
      >
        告诉我们你常用的支付场景和持有的卡，<br class="hidden sm:inline" />
        我们帮你挑出 <span class="text-foreground font-semibold">一张默认卡 + ≤5 条例外</span>，<br class="hidden sm:inline" />
        让你刷卡前不用打开 App。
      </p>

      <!-- 3 value props -->
      <div class="anim-fade-in-up mt-12 grid w-full max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3" style="animation-delay: 220ms;">
        <div class="rounded-2xl border bg-card/60 px-5 py-5 text-left backdrop-blur-sm">
          <CreditCard class="mb-3 size-5 text-primary" />
          <div class="text-sm font-semibold">默认卡 + 例外</div>
          <p class="mt-1.5 text-xs leading-relaxed text-muted-foreground">
            算法挑一张主力卡，配几条能背下来的例外。Pulse 思路，覆盖你 90%+ 的消费。
          </p>
        </div>
        <div class="rounded-2xl border bg-card/60 px-5 py-5 text-left backdrop-blur-sm">
          <Layers class="mb-3 size-5 text-primary" />
          <div class="text-sm font-semibold">5 个 bucket</div>
          <p class="mt-1.5 text-xs leading-relaxed text-muted-foreground">
            随手刷 · 这周薅 · 出门带 · 月初约 · 年度算。按"你什么时候要想它"分类，养习惯。
          </p>
        </div>
        <div class="rounded-2xl border bg-card/60 px-5 py-5 text-left backdrop-blur-sm">
          <Lock class="mb-3 size-5 text-primary" />
          <div class="text-sm font-semibold">数据只在你本地</div>
          <p class="mt-1.5 text-xs leading-relaxed text-muted-foreground">
            画像 / 卡包 / 反馈全部 localStorage，不上传。可导出锁屏壁纸保留主页。
          </p>
        </div>
      </div>

      <!-- CTAs -->
      <div class="anim-fade-in-up mt-12 flex flex-col items-center gap-3" style="animation-delay: 300ms;">
        {#if hasCompletedOnboarding}
          <Button size="lg" class="px-9" on:click={onContinue}>
            <Sparkles class="mr-2 size-4" />
            进入我的卡包
            <ArrowRight class="ml-2 size-4" />
          </Button>
          <button
            type="button"
            class="focus-amber inline-flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            on:click={onStart}
          >
            或者重新走一遍 5 问画像
          </button>
        {:else}
          <Button size="lg" class="px-9" on:click={onStart}>
            <Sparkles class="mr-2 size-4" />
            开始 · 90 秒搞定
            <ArrowRight class="ml-2 size-4" />
          </Button>
          <p class="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Clock class="size-3" />
            5 道选择题 · 没有注册 · 没有付费
          </p>
        {/if}
      </div>
    </main>

    <footer class="anim-fade-in-up border-t border-dashed border-foreground/10 pt-5 text-center text-[11px] text-muted-foreground" style="animation-delay: 380ms;">
      <p class="tracking-wide">✦ 魔法小卡 MVP · 你少做一个决策 ✦</p>
    </footer>
  </div>
</div>
